// TODO: Fix errors
import { FileUpload } from "@/features/files/FileUpload";
import { uploadSchedule, useTeam } from "@repo/api";
import { Button, Text, trashCanIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ElementsContainer,
  FlexDiv,
  ModalSheet,
  SectionContainer,
  useToast,
} from "@repo/ui";
import { isFileSizeError } from "@repo/utils";
import { type Toast, getFileUploadErrorInfo } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";

const getToastMessage = (
  updateToast: (toast: Toast) => void,
  errorRows: object,
  successfulRows: number[],
  error?: Error
) => {
  // no error rows
  if (isEmpty(errorRows)) {
    updateToast({
      title: t("prompt.schedule_uploaded"),
      message: t("prompt.rows_upload_success", {
        number: successfulRows.length,
      }),
    });
  }
  // no successful rows
  if (!isEmpty(errorRows) && isEmpty(successfulRows)) {
    updateToast({
      title: t("prompt.schedule_upload_error"),
      message: t("prompt.rows_upload_error", {
        number: Object.keys(errorRows).length,
      }),
      button: {
        title: t("action.copy_error_log"),
        action: () =>
          navigator.clipboard.writeText(JSON.stringify(errorRows, null, 2)),
      },
    });
  }

  // both error and successful rows
  if (!isEmpty(errorRows) && !isEmpty(successfulRows)) {
    updateToast({
      title: t("prompt.schedule_upload_success"),
      message: `${t("prompt.rows_upload_success", {
        number: successfulRows.length,
      })}
      ${t("prompt.rows_upload_error", {
        number: Object.keys(errorRows).length,
      })}`,
      button: {
        title: t("action.copy_error_log"),
        action: () =>
          navigator.clipboard.writeText(JSON.stringify(errorRows, null, 2)),
      },
    });
  }

  if (error) {
    const errorInfo = getFileUploadErrorInfo(error, {
      defaultTitleKey: "prompt.schedule_upload_error",
      defaultMessageKey: "prompt.schedule_upload_error_message",
    });

    updateToast({
      title: errorInfo.title,
      message: errorInfo.message,
      button: isFileSizeError(error)
        ? undefined
        : {
            title: t("action.copy_error_log"),
            action: () =>
              navigator.clipboard.writeText(JSON.stringify(error, null, 2)),
          },
    });
  }
};

export const UploadScheduleModal = ({
  isOpen,
  closeModal,
  selectedTeam,
}: {
  isOpen: boolean;
  closeModal: () => void;
  selectedTeam?: number;
}) => {
  const { data: team } = useTeam(selectedTeam);
  const [file, setFile] = useState<FileList | undefined>();
  const { updateToast } = useToast();

  const [uploading, setUploading] = useState(false);

  const onUpload = async (theFile: FileList) => {
    setUploading(true);
    try {
      const res = await uploadSchedule(theFile?.[0]);
      getToastMessage(updateToast, res.errorRows, res.successfulRows);
    } catch (e) {
      getToastMessage(updateToast, {}, [], e as Error);
    } finally {
      setUploading(false);
      setFile(undefined);
      closeModal();
    }
  };

  return (
    <ModalSheet
      title={t("action.upload_schedule")}
      visible={isOpen}
      onClose={closeModal}
      onBack={closeModal}
      footer={
        <Button
          text={t("action.upload")}
          type="primary"
          disabled={!file}
          onClick={() => {
            if (file) {
              onUpload(file);
            }
          }}
          loading={uploading}
        />
      }
    >
      <SectionContainer>
        <Text textStyle="Heading.S">{team?.teamName}</Text>
        <FileUpload
          upload={async (theFile) => {
            setFile(theFile);
            return Promise.resolve();
          }}
          accept=".xls, .xlsx"
        />
        {file?.[0] && (
          <ElementsContainer>
            <Text textStyle="Heading.XS">{t("Selected file")}</Text>
            <FlexDiv justifyContent="space-between" alignItems="center">
              <Text>{file?.[0].name}</Text>
              <Button
                size="small"
                iconOnly
                ssrIcon={trashCanIcon}
                onClick={() => {
                  setFile(undefined);
                }}
              />
            </FlexDiv>
          </ElementsContainer>
        )}
      </SectionContainer>
    </ModalSheet>
  );
};
